import React, { useCallback, useEffect, useState } from 'react';
import { Box, Container, Typography, Paper, TextField, Button, LinearProgress, Chip, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useTheme, useMediaQuery } from '@mui/material';
import { RefreshCcwIcon, DollarSignIcon, CheckCircleIcon, ClockIcon, XCircleIcon, AlertTriangleIcon, TrendingUpIcon, ArrowRightIcon, ShieldCheckIcon, GlobeIcon } from 'lucide-react';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
import KPICard from '../components/KPICard';
import ChartCard from '../components/ChartCard';
interface DashboardProps {
  onLogout: () => void;
}
export const Dashboard: React.FC<DashboardProps> = ({
  onLogout
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [dateFrom, setDateFrom] = useState('2026-04-17T00:00');
  const [dateTo, setDateTo] = useState('2026-04-24T23:59');
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const refreshData = useCallback(() => {
    setIsRefreshing(true);
    setTimeout(() => {
      setLastRefresh(new Date());
      setIsRefreshing(false);
    }, 1200);
  }, []);
  useEffect(() => {
    const interval = setInterval(refreshData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [refreshData]);
  // Mock data for the revamped dashboard
  const transactionSummary = {
    received: {
      value: '24,580',
      txnValue: 'USD 2.8M',
      trend: 12.3,
      data: [800, 900, 1050, 1100, 1200, 1250, 1300, 1350, 1400, 1450, 1500, 1580]
    },
    processed: {
      value: '23,120',
      txnValue: 'USD 2.65M',
      trend: 10.8,
      data: [750, 850, 1000, 1050, 1150, 1200, 1250, 1300, 1350, 1400, 1450, 1520]
    },
    pending: {
      value: '892',
      txnValue: 'USD 98K',
      trend: -5.2,
      data: [100, 95, 90, 88, 85, 82, 80, 78, 76, 75, 73, 72]
    },
    failed: {
      value: '568',
      txnValue: 'USD 67K',
      trend: -8.1,
      data: [80, 75, 70, 68, 65, 62, 60, 58, 56, 55, 54, 52]
    }
  };
  const successRate = (23120 / 24580 * 100).toFixed(1);
  const quickActions = [{
    label: '12 failed transactions need review',
    severity: 'error',
    link: '/failed-transactions'
  }, {
    label: '3 pending compliance holds',
    severity: 'warning',
    link: '/checker-inbox'
  }, {
    label: '5 high-value transactions awaiting approval',
    severity: 'info',
    link: '/maker-inbox'
  }];
  const balanceOverview = [{
    currency: 'GHS',
    symbol: '₵',
    available: '1,245,890.50',
    reserved: '45,230.00',
    lastUpdated: '2 min ago'
  }, {
    currency: 'NGN',
    symbol: '₦',
    available: '8,567,234.75',
    reserved: '234,500.00',
    lastUpdated: '2 min ago'
  }, {
    currency: 'USD',
    symbol: '$',
    available: '456,789.25',
    reserved: '12,340.00',
    lastUpdated: '2 min ago'
  }, {
    currency: 'EUR',
    symbol: '€',
    available: '234,567.80',
    reserved: '8,900.00',
    lastUpdated: '3 min ago'
  }, {
    currency: 'XOF',
    symbol: 'CFA',
    available: '12,345,678.00',
    reserved: '567,890.00',
    lastUpdated: '3 min ago'
  }];
  const lastSynced = '2 minutes ago';
  const complianceStatus = {
    kycCompletion: 87,
    amlPassRate: 94,
    pendingReviews: 23,
    amlFlags: 2
  };
  const regionMetrics = {
    ghana: {
      totalVolume: 'USD 1.2M',
      successRate: '94.2%',
      avgProcessingTime: '2.3 hrs',
      topCorridors: [{
        name: 'Ghana → UK',
        volume: 'USD 450K'
      }, {
        name: 'Ghana → US',
        volume: 'USD 380K'
      }, {
        name: 'Ghana → Canada',
        volume: 'USD 220K'
      }],
      topPartners: [{
        name: 'MoneyGram',
        volume: 'USD 520K'
      }, {
        name: 'Western Union',
        volume: 'USD 410K'
      }, {
        name: 'RIA',
        volume: 'USD 270K'
      }],
      trend: [100, 110, 105, 115, 120, 118, 125]
    },
    nigeria: {
      totalVolume: 'USD 1.6M',
      successRate: '92.8%',
      avgProcessingTime: '2.8 hrs',
      topCorridors: [{
        name: 'Nigeria → UK',
        volume: 'USD 580K'
      }, {
        name: 'Nigeria → US',
        volume: 'USD 520K'
      }, {
        name: 'Nigeria → UAE',
        volume: 'USD 340K'
      }],
      topPartners: [{
        name: 'Western Union',
        volume: 'USD 680K'
      }, {
        name: 'MoneyGram',
        volume: 'USD 550K'
      }, {
        name: 'WorldRemit',
        volume: 'USD 370K'
      }],
      trend: [120, 125, 130, 128, 135, 140, 145]
    }
  };
  const corridorPerformance = {
    categories: ['Ghana→UK', 'Nigeria→UK', 'Nigeria→US', 'Ghana→US', 'Nigeria→UAE'],
    series: [{
      name: 'Volume (USD K)',
      data: [450, 580, 520, 380, 340],
      color: '#0052CC'
    }]
  };
  const processingTimeData = {
    categories: ['Apr 18', 'Apr 19', 'Apr 20', 'Apr 21', 'Apr 22', 'Apr 23', 'Apr 24'],
    series: [{
      name: 'Avg Processing Time (hrs)',
      data: [3.2, 2.9, 2.7, 2.5, 2.4, 2.6, 2.5],
      color: '#0052CC'
    }]
  };
  const errorRateData = {
    categories: ['Apr 18', 'Apr 19', 'Apr 20', 'Apr 21', 'Apr 22', 'Apr 23', 'Apr 24'],
    series: [{
      name: 'MoneyGram',
      data: [3.2, 2.8, 2.5, 2.3, 2.1, 2.0, 1.9],
      color: '#0052CC'
    }, {
      name: 'Western Union',
      data: [2.8, 2.6, 2.4, 2.2, 2.0, 1.9, 1.8],
      color: '#0B7923'
    }, {
      name: 'RIA',
      data: [4.1, 3.8, 3.5, 3.2, 3.0, 2.8, 2.6],
      color: '#B38600'
    }]
  };
  return <Box sx={{
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: '#F8FAFC'
  }} data-id="element-1401">
      <TopBar onMenuClick={handleDrawerToggle} onLogout={onLogout} data-id="element-1402" />
      <Sidebar open={mobileOpen} onClose={handleDrawerToggle} activePage="dashboard" data-id="element-1403" />
      <Box component="main" sx={{
      flexGrow: 1,
      p: 3,
      pb: 10,
      width: {
        sm: 'calc(100% - 240px)'
      },
      ml: 0,
      mt: 8,
      position: 'relative'
    }} data-id="element-1404">
        {isRefreshing && <LinearProgress sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        zIndex: 9999,
        backgroundColor: '#E5E7EB',
        '& .MuiLinearProgress-bar': {
          backgroundColor: '#0052CC'
        }
      }} data-id="element-1405" />}

        <Container maxWidth={false} sx={{
        maxWidth: 1600
      }} data-id="element-1406">
          {/* Header Row */}
          <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2
        }} data-id="element-1407">
            <Box data-id="element-1408">
              <Typography variant="h4" sx={{
              fontWeight: 700,
              color: '#1F2937',
              fontSize: '2rem',
              mb: 0.5
            }} data-id="element-1409">
                Partner Portal Dashboard
              </Typography>
              <Typography variant="body2" sx={{
              color: '#6B7280',
              fontSize: '0.95rem'
            }} data-id="element-1410">
                Ecobank Remittance Backoffice — Real-time insights and analytics
              </Typography>
            </Box>
            <Box sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            flexWrap: 'wrap'
          }} data-id="element-1411">
              <TextField label="From" type="datetime-local" size="small" value={dateFrom} onChange={e => setDateFrom(e.target.value)} InputLabelProps={{
              shrink: true
            }} sx={{
              width: 200,
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px',
                fontSize: '0.85rem',
                backgroundColor: 'white'
              }
            }} data-id="element-1412" />
              <TextField label="To" type="datetime-local" size="small" value={dateTo} onChange={e => setDateTo(e.target.value)} InputLabelProps={{
              shrink: true
            }} sx={{
              width: 200,
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px',
                fontSize: '0.85rem',
                backgroundColor: 'white'
              }
            }} data-id="element-1413" />
              <Button variant="contained" startIcon={<RefreshCcwIcon size={16} data-id="element-1415" />} onClick={refreshData} sx={{
              backgroundColor: '#0052CC',
              textTransform: 'none',
              borderRadius: '6px',
              boxShadow: 'none',
              px: 3,
              '&:hover': {
                backgroundColor: '#003DA8',
                boxShadow: 'none'
              }
            }} data-id="element-1414">
                Refresh
              </Button>
            </Box>
          </Box>

          {/* Section 1: Transaction Summary */}
          <Typography variant="overline" sx={{
          fontWeight: 600,
          color: '#6B7280',
          mb: 1.5,
          display: 'block',
          letterSpacing: '0.05em'
        }} data-id="element-1416">
            TRANSACTION SUMMARY
          </Typography>
          <Grid container spacing={2} sx={{
          mb: 3
        }} data-id="element-1417">
            <Grid item xs={12} sm={6} md={3} data-id="element-1418">
              <KPICard title="TRANSACTIONS RECEIVED" value={transactionSummary.received.value} icon={<DollarSignIcon size={20} data-id="element-1420" />} color="#0052CC" subDetails={[{
              label: 'Total Value',
              value: transactionSummary.received.txnValue
            }, {
              label: 'Success Rate',
              value: `${successRate}%`
            }]} trend={{
              value: transactionSummary.received.trend,
              label: '',
              data: transactionSummary.received.data
            }} data-id="element-1419" />
            </Grid>
            <Grid item xs={12} sm={6} md={3} data-id="element-1421">
              <KPICard title="SUCCESSFULLY PROCESSED" value={transactionSummary.processed.value} icon={<CheckCircleIcon size={20} data-id="element-1423" />} color="#0B7923" subDetails={[{
              label: 'Total Value',
              value: transactionSummary.processed.txnValue
            }, {
              label: 'Of Total',
              value: `${successRate}%`
            }]} trend={{
              value: transactionSummary.processed.trend,
              label: '',
              data: transactionSummary.processed.data
            }} data-id="element-1422" />
            </Grid>
            <Grid item xs={12} sm={6} md={3} data-id="element-1424">
              <KPICard title="PENDING TRANSACTIONS" value={transactionSummary.pending.value} icon={<ClockIcon size={20} data-id="element-1426" />} color="#B38600" subDetails={[{
              label: 'Total Value',
              value: transactionSummary.pending.txnValue
            }, {
              label: 'Of Total',
              value: '3.6%'
            }]} trend={{
              value: transactionSummary.pending.trend,
              label: '',
              data: transactionSummary.pending.data
            }} data-id="element-1425" />
            </Grid>
            <Grid item xs={12} sm={6} md={3} data-id="element-1427">
              <KPICard title="FAILED TRANSACTIONS" value={transactionSummary.failed.value} icon={<XCircleIcon size={20} data-id="element-1429" />} color="#AE2A19" subDetails={[{
              label: 'Total Value',
              value: transactionSummary.failed.txnValue
            }, {
              label: 'Of Total',
              value: '2.3%'
            }]} trend={{
              value: transactionSummary.failed.trend,
              label: '',
              data: transactionSummary.failed.data
            }} data-id="element-1428" />
            </Grid>
          </Grid>

          {/* Quick Actions Panel */}
          <Paper sx={{
          p: 2.5,
          borderRadius: '8px',
          border: '1px solid #E5E7EB',
          boxShadow: '0px 1px 2px rgba(0,0,0,0.05)',
          backgroundColor: 'white',
          mb: 4
        }} data-id="element-1430">
            <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 2
          }} data-id="element-1431">
              <AlertTriangleIcon size={18} color="#B38600" data-id="element-1432" />
              <Typography variant="subtitle1" sx={{
              fontWeight: 700,
              color: '#1F2937',
              fontSize: '0.95rem'
            }} data-id="element-1433">
                Quick Actions Required
              </Typography>
            </Box>
            <Box sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 2
          }} data-id="element-1434">
              {quickActions.map((action, idx) => <Box key={idx} sx={{
              flex: 1,
              p: 2,
              borderRadius: '6px',
              border: '1px solid #E5E7EB',
              backgroundColor: '#F8F9FA',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2
            }} data-id="element-1435">
                  <Typography variant="body2" sx={{
                color: '#1F2937',
                fontWeight: 500,
                fontSize: '0.85rem'
              }} data-id="element-1436">
                    {action.label}
                  </Typography>
                  <Button size="small" endIcon={<ArrowRightIcon size={14} data-id="element-1438" />} sx={{
                textTransform: 'none',
                color: '#0052CC',
                fontWeight: 600,
                fontSize: '0.8rem',
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline'
                }
              }} data-id="element-1437">
                    Review
                  </Button>
                </Box>)}
            </Box>
          </Paper>

          {/* Section 2: Balance Overview + Compliance Status */}
          <Typography variant="overline" sx={{
          fontWeight: 600,
          color: '#6B7280',
          mb: 1.5,
          display: 'block',
          letterSpacing: '0.05em'
        }} data-id="element-1439">
            BALANCE & COMPLIANCE
          </Typography>
          <Grid container spacing={3} sx={{
          mb: 4
        }} data-id="element-1440">
            <Grid item xs={12} md={7} data-id="element-1441">
              <Paper sx={{
              p: 3,
              borderRadius: '8px',
              border: '1px solid #E5E7EB',
              boxShadow: '0px 1px 2px rgba(0,0,0,0.05)',
              backgroundColor: 'white',
              height: '100%'
            }} data-id="element-1442">
                <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
              }} data-id="element-1443">
                  <Typography variant="subtitle1" sx={{
                  fontWeight: 700,
                  color: '#1F2937',
                  fontSize: '1rem'
                }} data-id="element-1444">
                    Multi-Currency Balance Overview
                  </Typography>
                  <Chip label={`Last synced: ${lastSynced}`} size="small" sx={{
                  backgroundColor: '#F0F3F7',
                  color: '#6B7280',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  height: 24
                }} data-id="element-1445" />
                </Box>
                <TableContainer data-id="element-1446">
                  <Table size="small" data-id="element-1447">
                    <TableHead data-id="element-1448">
                      <TableRow data-id="element-1449">
                        <TableCell sx={{
                        fontWeight: 700,
                        color: '#6B7280',
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        borderBottom: '2px solid #E5E7EB',
                        backgroundColor: '#F8F9FA'
                      }} data-id="element-1450">
                          Currency
                        </TableCell>
                        <TableCell align="right" sx={{
                        fontWeight: 700,
                        color: '#6B7280',
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        borderBottom: '2px solid #E5E7EB',
                        backgroundColor: '#F8F9FA'
                      }} data-id="element-1451">
                          Available Balance
                        </TableCell>
                        <TableCell align="right" sx={{
                        fontWeight: 700,
                        color: '#6B7280',
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        borderBottom: '2px solid #E5E7EB',
                        backgroundColor: '#F8F9FA'
                      }} data-id="element-1452">
                          Reserved
                        </TableCell>
                        <TableCell align="right" sx={{
                        fontWeight: 700,
                        color: '#6B7280',
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        borderBottom: '2px solid #E5E7EB',
                        backgroundColor: '#F8F9FA'
                      }} data-id="element-1453">
                          Updated
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody data-id="element-1454">
                      {balanceOverview.map((row, idx) => <TableRow key={idx} sx={{
                      '&:hover': {
                        backgroundColor: '#F8F9FA'
                      }
                    }} data-id="element-1455">
                          <TableCell sx={{
                        borderBottom: '1px solid #E5E7EB'
                      }} data-id="element-1456">
                            <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }} data-id="element-1457">
                              <Box sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '6px',
                            backgroundColor: '#F0F3F7',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            color: '#0052CC'
                          }} data-id="element-1458">
                                {row.currency}
                              </Box>
                              <Typography variant="body2" sx={{
                            fontWeight: 600,
                            color: '#1F2937',
                            fontSize: '0.85rem'
                          }} data-id="element-1459">
                                {row.symbol}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{
                        fontWeight: 700,
                        color: '#1F2937',
                        fontSize: '0.9rem',
                        borderBottom: '1px solid #E5E7EB'
                      }} data-id="element-1460">
                            {row.symbol} {row.available}
                          </TableCell>
                          <TableCell align="right" sx={{
                        color: '#6B7280',
                        fontSize: '0.85rem',
                        borderBottom: '1px solid #E5E7EB'
                      }} data-id="element-1461">
                            {row.symbol} {row.reserved}
                          </TableCell>
                          <TableCell align="right" sx={{
                        color: '#6B7280',
                        fontSize: '0.75rem',
                        borderBottom: '1px solid #E5E7EB'
                      }} data-id="element-1462">
                            {row.lastUpdated}
                          </TableCell>
                        </TableRow>)}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={5} data-id="element-1463">
              <Paper sx={{
              p: 3,
              borderRadius: '8px',
              border: '1px solid #E5E7EB',
              boxShadow: '0px 1px 2px rgba(0,0,0,0.05)',
              backgroundColor: 'white',
              height: '100%'
            }} data-id="element-1464">
                <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 2
              }} data-id="element-1465">
                  <ShieldCheckIcon size={18} color="#0052CC" data-id="element-1466" />
                  <Typography variant="subtitle1" sx={{
                  fontWeight: 700,
                  color: '#1F2937',
                  fontSize: '1rem'
                }} data-id="element-1467">
                    Compliance Status
                  </Typography>
                </Box>
                <Box sx={{
                mb: 3
              }} data-id="element-1468">
                  <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 1
                }} data-id="element-1469">
                    <Typography variant="body2" sx={{
                    color: '#1F2937',
                    fontWeight: 500,
                    fontSize: '0.85rem'
                  }} data-id="element-1470">
                      KYC Completion Rate
                    </Typography>
                    <Typography variant="body2" sx={{
                    fontWeight: 700,
                    color: '#0B7923',
                    fontSize: '0.85rem'
                  }} data-id="element-1471">
                      {complianceStatus.kycCompletion}%
                    </Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={complianceStatus.kycCompletion} sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: '#E5E7EB',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    backgroundColor: '#0B7923'
                  }
                }} data-id="element-1472" />
                </Box>
                <Box sx={{
                mb: 3
              }} data-id="element-1473">
                  <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 1
                }} data-id="element-1474">
                    <Typography variant="body2" sx={{
                    color: '#1F2937',
                    fontWeight: 500,
                    fontSize: '0.85rem'
                  }} data-id="element-1475">
                      AML Screening Pass Rate
                    </Typography>
                    <Typography variant="body2" sx={{
                    fontWeight: 700,
                    color: '#0B7923',
                    fontSize: '0.85rem'
                  }} data-id="element-1476">
                      {complianceStatus.amlPassRate}%
                    </Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={complianceStatus.amlPassRate} sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: '#E5E7EB',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    backgroundColor: '#0B7923'
                  }
                }} data-id="element-1477" />
                </Box>
                <Box sx={{
                display: 'flex',
                gap: 2,
                mt: 3
              }} data-id="element-1478">
                  <Box sx={{
                  flex: 1,
                  p: 2,
                  borderRadius: '6px',
                  backgroundColor: '#FEF3C7',
                  border: '1px solid #FDE68A'
                }} data-id="element-1479">
                    <Typography variant="caption" sx={{
                    color: '#92400E',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    display: 'block',
                    mb: 0.5
                  }} data-id="element-1480">
                      Pending Reviews
                    </Typography>
                    <Typography variant="h5" sx={{
                    fontWeight: 700,
                    color: '#92400E',
                    fontSize: '1.5rem'
                  }} data-id="element-1481">
                      {complianceStatus.pendingReviews}
                    </Typography>
                  </Box>
                  <Box sx={{
                  flex: 1,
                  p: 2,
                  borderRadius: '6px',
                  backgroundColor: '#FEE2E2',
                  border: '1px solid #FECACA'
                }} data-id="element-1482">
                    <Typography variant="caption" sx={{
                    color: '#991B1B',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    display: 'block',
                    mb: 0.5
                  }} data-id="element-1483">
                      AML Flags
                    </Typography>
                    <Typography variant="h5" sx={{
                    fontWeight: 700,
                    color: '#991B1B',
                    fontSize: '1.5rem'
                  }} data-id="element-1484">
                      {complianceStatus.amlFlags}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Section 3: Region-Specific Metrics */}
          <Typography variant="overline" sx={{
          fontWeight: 600,
          color: '#6B7280',
          mb: 1.5,
          display: 'block',
          letterSpacing: '0.05em'
        }} data-id="element-1485">
            REGIONAL PERFORMANCE
          </Typography>
          <Grid container spacing={3} sx={{
          mb: 4
        }} data-id="element-1486">
            {/* Ghana */}
            <Grid item xs={12} md={6} data-id="element-1487">
              <Paper sx={{
              p: 3,
              borderRadius: '8px',
              border: '1px solid #E5E7EB',
              boxShadow: '0px 1px 2px rgba(0,0,0,0.05)',
              backgroundColor: 'white',
              height: '100%'
            }} data-id="element-1488">
                <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 2
              }} data-id="element-1489">
                  <Box sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '8px',
                  backgroundColor: '#F0F3F7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }} data-id="element-1490">
                    <GlobeIcon size={20} color="#0052CC" data-id="element-1491" />
                  </Box>
                  <Box data-id="element-1492">
                    <Typography variant="subtitle1" sx={{
                    fontWeight: 700,
                    color: '#1F2937',
                    fontSize: '1rem'
                  }} data-id="element-1493">
                      Ghana
                    </Typography>
                    <Typography variant="caption" sx={{
                    color: '#6B7280',
                    fontSize: '0.75rem'
                  }} data-id="element-1494">
                      West Africa Region
                    </Typography>
                  </Box>
                </Box>
                <Grid container spacing={2} sx={{
                mb: 2
              }} data-id="element-1495">
                  <Grid item xs={6} data-id="element-1496">
                    <Box sx={{
                    p: 1.5,
                    borderRadius: '6px',
                    backgroundColor: '#F8F9FA',
                    border: '1px solid #E5E7EB'
                  }} data-id="element-1497">
                      <Typography variant="caption" sx={{
                      color: '#6B7280',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      display: 'block',
                      mb: 0.5
                    }} data-id="element-1498">
                        Total Volume
                      </Typography>
                      <Typography variant="h6" sx={{
                      fontWeight: 700,
                      color: '#1F2937',
                      fontSize: '1.1rem'
                    }} data-id="element-1499">
                        {regionMetrics.ghana.totalVolume}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} data-id="element-1500">
                    <Box sx={{
                    p: 1.5,
                    borderRadius: '6px',
                    backgroundColor: '#F8F9FA',
                    border: '1px solid #E5E7EB'
                  }} data-id="element-1501">
                      <Typography variant="caption" sx={{
                      color: '#6B7280',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      display: 'block',
                      mb: 0.5
                    }} data-id="element-1502">
                        Success Rate
                      </Typography>
                      <Typography variant="h6" sx={{
                      fontWeight: 700,
                      color: '#0B7923',
                      fontSize: '1.1rem'
                    }} data-id="element-1503">
                        {regionMetrics.ghana.successRate}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} data-id="element-1504">
                    <Box sx={{
                    p: 1.5,
                    borderRadius: '6px',
                    backgroundColor: '#F8F9FA',
                    border: '1px solid #E5E7EB'
                  }} data-id="element-1505">
                      <Typography variant="caption" sx={{
                      color: '#6B7280',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      display: 'block',
                      mb: 0.5
                    }} data-id="element-1506">
                        Avg Processing Time
                      </Typography>
                      <Typography variant="h6" sx={{
                      fontWeight: 700,
                      color: '#1F2937',
                      fontSize: '1.1rem'
                    }} data-id="element-1507">
                        {regionMetrics.ghana.avgProcessingTime}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                <Box sx={{
                mb: 2
              }} data-id="element-1508">
                  <Typography variant="caption" sx={{
                  color: '#6B7280',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  display: 'block',
                  mb: 1
                }} data-id="element-1509">
                    Top Corridors
                  </Typography>
                  {regionMetrics.ghana.topCorridors.map((corridor, idx) => <Box key={idx} sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 0.75,
                  borderBottom: idx < 2 ? '1px solid #E5E7EB' : 'none'
                }} data-id="element-1510">
                      <Typography variant="body2" sx={{
                    color: '#1F2937',
                    fontSize: '0.85rem',
                    fontWeight: 500
                  }} data-id="element-1511">
                        {corridor.name}
                      </Typography>
                      <Typography variant="body2" sx={{
                    color: '#6B7280',
                    fontSize: '0.85rem',
                    fontWeight: 600
                  }} data-id="element-1512">
                        {corridor.volume}
                      </Typography>
                    </Box>)}
                </Box>
                <Box data-id="element-1513">
                  <Typography variant="caption" sx={{
                  color: '#6B7280',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  display: 'block',
                  mb: 1
                }} data-id="element-1514">
                    Top Partners by Volume
                  </Typography>
                  {regionMetrics.ghana.topPartners.map((partner, idx) => <Box key={idx} sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 0.75,
                  borderBottom: idx < 2 ? '1px solid #E5E7EB' : 'none'
                }} data-id="element-1515">
                      <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }} data-id="element-1516">
                        <Chip label={`#${idx + 1}`} size="small" sx={{
                      backgroundColor: '#F0F3F7',
                      color: '#0052CC',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      height: 20,
                      minWidth: 28
                    }} data-id="element-1517" />
                        <Typography variant="body2" sx={{
                      color: '#1F2937',
                      fontSize: '0.85rem',
                      fontWeight: 500
                    }} data-id="element-1518">
                          {partner.name}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{
                    color: '#6B7280',
                    fontSize: '0.85rem',
                    fontWeight: 600
                  }} data-id="element-1519">
                        {partner.volume}
                      </Typography>
                    </Box>)}
                </Box>
              </Paper>
            </Grid>

            {/* Nigeria */}
            <Grid item xs={12} md={6} data-id="element-1520">
              <Paper sx={{
              p: 3,
              borderRadius: '8px',
              border: '1px solid #E5E7EB',
              boxShadow: '0px 1px 2px rgba(0,0,0,0.05)',
              backgroundColor: 'white',
              height: '100%'
            }} data-id="element-1521">
                <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 2
              }} data-id="element-1522">
                  <Box sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '8px',
                  backgroundColor: '#F0F3F7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }} data-id="element-1523">
                    <GlobeIcon size={20} color="#0052CC" data-id="element-1524" />
                  </Box>
                  <Box data-id="element-1525">
                    <Typography variant="subtitle1" sx={{
                    fontWeight: 700,
                    color: '#1F2937',
                    fontSize: '1rem'
                  }} data-id="element-1526">
                      Nigeria
                    </Typography>
                    <Typography variant="caption" sx={{
                    color: '#6B7280',
                    fontSize: '0.75rem'
                  }} data-id="element-1527">
                      West Africa Region
                    </Typography>
                  </Box>
                </Box>
                <Grid container spacing={2} sx={{
                mb: 2
              }} data-id="element-1528">
                  <Grid item xs={6} data-id="element-1529">
                    <Box sx={{
                    p: 1.5,
                    borderRadius: '6px',
                    backgroundColor: '#F8F9FA',
                    border: '1px solid #E5E7EB'
                  }} data-id="element-1530">
                      <Typography variant="caption" sx={{
                      color: '#6B7280',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      display: 'block',
                      mb: 0.5
                    }} data-id="element-1531">
                        Total Volume
                      </Typography>
                      <Typography variant="h6" sx={{
                      fontWeight: 700,
                      color: '#1F2937',
                      fontSize: '1.1rem'
                    }} data-id="element-1532">
                        {regionMetrics.nigeria.totalVolume}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} data-id="element-1533">
                    <Box sx={{
                    p: 1.5,
                    borderRadius: '6px',
                    backgroundColor: '#F8F9FA',
                    border: '1px solid #E5E7EB'
                  }} data-id="element-1534">
                      <Typography variant="caption" sx={{
                      color: '#6B7280',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      display: 'block',
                      mb: 0.5
                    }} data-id="element-1535">
                        Success Rate
                      </Typography>
                      <Typography variant="h6" sx={{
                      fontWeight: 700,
                      color: '#0B7923',
                      fontSize: '1.1rem'
                    }} data-id="element-1536">
                        {regionMetrics.nigeria.successRate}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} data-id="element-1537">
                    <Box sx={{
                    p: 1.5,
                    borderRadius: '6px',
                    backgroundColor: '#F8F9FA',
                    border: '1px solid #E5E7EB'
                  }} data-id="element-1538">
                      <Typography variant="caption" sx={{
                      color: '#6B7280',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      display: 'block',
                      mb: 0.5
                    }} data-id="element-1539">
                        Avg Processing Time
                      </Typography>
                      <Typography variant="h6" sx={{
                      fontWeight: 700,
                      color: '#1F2937',
                      fontSize: '1.1rem'
                    }} data-id="element-1540">
                        {regionMetrics.nigeria.avgProcessingTime}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                <Box sx={{
                mb: 2
              }} data-id="element-1541">
                  <Typography variant="caption" sx={{
                  color: '#6B7280',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  display: 'block',
                  mb: 1
                }} data-id="element-1542">
                    Top Corridors
                  </Typography>
                  {regionMetrics.nigeria.topCorridors.map((corridor, idx) => <Box key={idx} sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 0.75,
                  borderBottom: idx < 2 ? '1px solid #E5E7EB' : 'none'
                }} data-id="element-1543">
                      <Typography variant="body2" sx={{
                    color: '#1F2937',
                    fontSize: '0.85rem',
                    fontWeight: 500
                  }} data-id="element-1544">
                        {corridor.name}
                      </Typography>
                      <Typography variant="body2" sx={{
                    color: '#6B7280',
                    fontSize: '0.85rem',
                    fontWeight: 600
                  }} data-id="element-1545">
                        {corridor.volume}
                      </Typography>
                    </Box>)}
                </Box>
                <Box data-id="element-1546">
                  <Typography variant="caption" sx={{
                  color: '#6B7280',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  display: 'block',
                  mb: 1
                }} data-id="element-1547">
                    Top Partners by Volume
                  </Typography>
                  {regionMetrics.nigeria.topPartners.map((partner, idx) => <Box key={idx} sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 0.75,
                  borderBottom: idx < 2 ? '1px solid #E5E7EB' : 'none'
                }} data-id="element-1548">
                      <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }} data-id="element-1549">
                        <Chip label={`#${idx + 1}`} size="small" sx={{
                      backgroundColor: '#F0F3F7',
                      color: '#0052CC',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      height: 20,
                      minWidth: 28
                    }} data-id="element-1550" />
                        <Typography variant="body2" sx={{
                      color: '#1F2937',
                      fontSize: '0.85rem',
                      fontWeight: 500
                    }} data-id="element-1551">
                          {partner.name}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{
                    color: '#6B7280',
                    fontSize: '0.85rem',
                    fontWeight: 600
                  }} data-id="element-1552">
                        {partner.volume}
                      </Typography>
                    </Box>)}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Section 4: Analytics & Insights */}
          <Typography variant="overline" sx={{
          fontWeight: 600,
          color: '#6B7280',
          mb: 1.5,
          display: 'block',
          letterSpacing: '0.05em'
        }} data-id="element-1553">
            ANALYTICS & INSIGHTS
          </Typography>
          <Grid container spacing={3} data-id="element-1554">
            <Grid item xs={12} md={4} data-id="element-1555">
              <ChartCard title="Top Corridors by Volume" type="stackedBar" data={corridorPerformance} height={280} data-id="element-1556" />
            </Grid>
            <Grid item xs={12} md={4} data-id="element-1557">
              <ChartCard title="Average Processing Time (7 Days)" type="area" data={processingTimeData} height={280} data-id="element-1558" />
            </Grid>
            <Grid item xs={12} md={4} data-id="element-1559">
              <ChartCard title="Error Rate by Partner" type="line" data={errorRateData} height={280} data-id="element-1560" />
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>;
};
export default Dashboard;